import React, { useState } from 'react';
import { downloadDonorExport } from '../../lib/api';
import { formatAccountName } from '../../utils/formatters';
import { SmsAudiences } from './SmsAudiences';
import './DonorExport.css';

interface DonorExportProps {
  accounts: string[];
  defaultAccount?: string;
  onClose: () => void;
}

export const DonorExport: React.FC<DonorExportProps> = ({ accounts, defaultAccount, onClose }) => {
  const [account, setAccount] = useState(defaultAccount || accounts[0] || '');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const handleExport = async () => {
    if (!account) return;
    setError(null);
    setDone(false);
    setDownloading(true);
    try {
      await downloadDonorExport(account);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="donor-export">
      <div className="donor-export-header">
        <h2>Donor Export</h2>
        <button className="btn btn-secondary" onClick={onClose}>Back to dashboard</button>
      </div>

      <p className="donor-export-desc">
        Download every donor for an account with their total donation amount, pulled at the
        broadcast level (all-time). Superadmin only.
      </p>

      <div className="donor-export-controls">
        <label htmlFor="donor-export-account">Account</label>
        <select
          id="donor-export-account"
          value={account}
          onChange={(e) => { setAccount(e.target.value); setDone(false); setError(null); }}
        >
          {accounts.map((a) => (
            <option key={a} value={a}>{formatAccountName(a)}</option>
          ))}
        </select>

        <button
          className="btn btn-primary"
          disabled={!account || downloading}
          onClick={handleExport}
        >
          {downloading ? 'Exporting…' : 'Export donors'}
        </button>
      </div>

      {downloading && (
        <p className="donor-export-note">Running the query — large accounts can take a moment.</p>
      )}
      {done && !error && (
        <p className="donor-export-success">Download started: {account}_donors.csv</p>
      )}
      {error && <p className="donor-export-error">Error: {error}</p>}

      <SmsAudiences />
    </div>
  );
};
